import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  SimScenario,
  SimScenarioInput,
  SimScenarioFilters,
  SimRun,
  SimRunDetail,
  SimBatch,
  SimBatchDetail,
  SimSummary,
  SimRunRequest,
  SimRunFilters,
  SimSummaryFilters,
  SimTag,
  SimTagInput,
} from '@/types/simulation'

/**
 * Query keys for simulation queries
 */
export const simulationKeys = {
  all: ['simulations'] as const,
  tags: () => [...simulationKeys.all, 'tags'] as const,
  scenarios: () => [...simulationKeys.all, 'scenarios'] as const,
  scenarioList: (filters?: SimScenarioFilters) =>
    [...simulationKeys.scenarios(), filters] as const,
  scenario: (id: number) => [...simulationKeys.scenarios(), id] as const,
  runs: () => [...simulationKeys.all, 'runs'] as const,
  runList: (filters?: SimRunFilters) =>
    [...simulationKeys.runs(), filters] as const,
  run: (id: number) => [...simulationKeys.runs(), id] as const,
  batches: () => [...simulationKeys.all, 'batches'] as const,
  batchList: (params?: { page?: number; perPage?: number }) =>
    [...simulationKeys.batches(), params] as const,
  batch: (id: number) => [...simulationKeys.batches(), id] as const,
  summary: (filters?: SimSummaryFilters) =>
    [...simulationKeys.all, 'summary', filters] as const,
}

// ============================================================================
// Tag Hooks
// ============================================================================

interface UseSimTagsParams {
  enabled?: boolean
}

/**
 * Hook for fetching simulation tags
 */
export function useSimTags({ enabled = true }: UseSimTagsParams = {}) {
  return useQuery<SimTag[]>({
    queryKey: simulationKeys.tags(),
    queryFn: async () => {
      const response = await api.get<SimTag[]>('/simulations/tags')
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for creating a new simulation tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SimTagInput) => {
      const response = await api.post<SimTag>('/simulations/tags', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.tags() })
    },
  })
}

/**
 * Hook for deleting a simulation tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/simulations/tags/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.tags() })
      queryClient.invalidateQueries({ queryKey: simulationKeys.scenarios() })
    },
  })
}

// ============================================================================
// Scenario Hooks
// ============================================================================

interface UseSimScenariosParams {
  tagIds?: number[]
  enabled?: boolean
}

/**
 * Hook for fetching simulation scenarios
 */
export function useSimScenarios({
  tagIds,
  enabled = true,
}: UseSimScenariosParams = {}) {
  return useQuery<SimScenario[]>({
    queryKey: simulationKeys.scenarioList({ tag_ids: tagIds }),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (tagIds?.length) {
        params.tag_ids = tagIds
      }
      const response = await api.get<SimScenario[]>('/simulations/scenarios', {
        params,
      })
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

interface UseSimScenarioParams {
  id: number
  enabled?: boolean
}

/**
 * Hook for fetching a single simulation scenario
 */
export function useSimScenario({ id, enabled = true }: UseSimScenarioParams) {
  return useQuery<SimScenario>({
    queryKey: simulationKeys.scenario(id),
    queryFn: async () => {
      const response = await api.get<SimScenario>(
        `/simulations/scenarios/${id}`
      )
      return response.data
    },
    enabled: enabled && id > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for creating a new simulation scenario
 */
export function useCreateScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SimScenarioInput) => {
      const response = await api.post<SimScenario>(
        '/simulations/scenarios',
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.scenarios() })
    },
  })
}

/**
 * Hook for updating a simulation scenario
 */
export function useUpdateScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: Partial<SimScenarioInput>
    }) => {
      const response = await api.put<SimScenario>(
        `/simulations/scenarios/${id}`,
        data
      )
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.scenarios() })
      queryClient.invalidateQueries({ queryKey: simulationKeys.scenario(id) })
    },
  })
}

/**
 * Hook for deleting a simulation scenario
 */
export function useDeleteScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/simulations/scenarios/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.scenarios() })
    },
  })
}

// ============================================================================
// Run Hooks
// ============================================================================

interface UseSimRunsParams {
  filters?: SimRunFilters
  enabled?: boolean
  /** Poll interval in ms while runs are pending/running (default: 3000). Set to false to disable. */
  pollInterval?: number | false
}

/**
 * Hook for fetching simulation runs
 *
 * Automatically polls for updates while there are pending or running runs.
 */
export function useSimRuns({
  filters,
  enabled = true,
  pollInterval = 3000,
}: UseSimRunsParams = {}) {
  return useQuery<SimRun[]>({
    queryKey: simulationKeys.runList(filters),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters?.scenario_id) params.scenario_id = filters.scenario_id
      if (filters?.assistant_id) params.assistant_id = filters.assistant_id
      if (filters?.batch_id) params.batch_id = filters.batch_id
      if (filters?.status) params.status = filters.status
      if (filters?.limit) params.limit = filters.limit

      const response = await api.get<SimRun[]>('/simulations/runs', {
        params,
      })
      return response.data
    },
    enabled,
    staleTime: 5 * 1000, // 5 seconds - runs can change frequently
    // Keep previous data while refetching to prevent flickering
    placeholderData: keepPreviousData,
    // Poll while there are pending or running runs
    refetchInterval: (query) => {
      if (!pollInterval) return false
      const data = query.state.data
      if (!data || data.length === 0) return false
      // Check if any runs are still pending or running
      const hasActiveRuns = data.some(
        (run) => run.status === 'pending' || run.status === 'running'
      )
      return hasActiveRuns ? pollInterval : false
    },
  })
}

interface UseSimRunParams {
  id: number
  enabled?: boolean
}

/**
 * Hook for fetching a single simulation run with details
 */
export function useSimRun({ id, enabled = true }: UseSimRunParams) {
  return useQuery<SimRunDetail>({
    queryKey: simulationKeys.run(id),
    queryFn: async () => {
      const response = await api.get<SimRunDetail>(
        `/simulations/runs/${id}`
      )
      return response.data
    },
    enabled: enabled && id > 0,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook for running simulations (starts background execution)
 *
 * Returns a batch object. Use useSimBatch with the batch ID to poll for progress.
 */
export function useRunSimulation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: SimRunRequest) => {
      const response = await api.post<SimBatch>('/simulations/run', request)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.batches() })
      queryClient.invalidateQueries({ queryKey: simulationKeys.runs() })
    },
  })
}

// ============================================================================
// Batch Hooks
// ============================================================================

interface UseSimBatchesParams {
  page?: number
  perPage?: number
  enabled?: boolean
  /** Poll interval in ms while batches are active (default: 3000). Set to false to disable. */
  pollInterval?: number | false
}

interface SimBatchListResponse {
  data: SimBatch[]
  total: number
  page: number
  per_page: number
}

/**
 * Hook for fetching simulation batches with pagination
 *
 * Automatically polls for updates while there are pending or running batches.
 */
export function useSimBatches({
  page = 1,
  perPage = 25,
  enabled = true,
  pollInterval = 3000,
}: UseSimBatchesParams = {}) {
  return useQuery<SimBatchListResponse>({
    queryKey: simulationKeys.batchList({ page, perPage }),
    queryFn: async () => {
      const response = await api.get<SimBatchListResponse>('/simulations/batches', {
        params: { page, per_page: perPage },
      })
      return response.data
    },
    enabled,
    staleTime: 5 * 1000, // 5 seconds - batches can change frequently
    placeholderData: keepPreviousData,
    // Poll while there are pending or running batches
    refetchInterval: (query) => {
      if (!pollInterval) return false
      const data = query.state.data
      if (!data || data.data.length === 0) return false
      const hasActiveBatches = data.data.some(
        (batch) => batch.status === 'pending' || batch.status === 'running'
      )
      return hasActiveBatches ? pollInterval : false
    },
  })
}

interface UseSimBatchParams {
  id: number
  enabled?: boolean
  /** Poll interval in ms while batch is running (default: 2000). Set to false to disable. */
  pollInterval?: number | false
}

/**
 * Hook for fetching a single simulation batch with details.
 *
 * Automatically polls for updates while the batch is pending or running.
 * Polling stops once the batch completes or fails.
 */
export function useSimBatch({
  id,
  enabled = true,
  pollInterval = 2000,
}: UseSimBatchParams) {
  const queryClient = useQueryClient()

  return useQuery<SimBatchDetail>({
    queryKey: simulationKeys.batch(id),
    queryFn: async () => {
      const response = await api.get<SimBatchDetail>(
        `/simulations/batches/${id}`
      )
      return response.data
    },
    enabled: enabled && id > 0,
    staleTime: 1000, // 1 second - batch status changes frequently
    // Poll while batch is in progress
    refetchInterval: (query) => {
      if (!pollInterval) return false
      const data = query.state.data
      if (!data) return pollInterval
      // Stop polling when batch is completed or failed
      if (data.status === 'completed' || data.status === 'failed') {
        // Invalidate related queries when batch completes
        queryClient.invalidateQueries({ queryKey: simulationKeys.runs() })
        queryClient.invalidateQueries({ queryKey: simulationKeys.summary() })
        return false
      }
      return pollInterval
    },
  })
}

// ============================================================================
// Summary Hooks
// ============================================================================

interface UseSimSummaryParams {
  filters?: SimSummaryFilters
  enabled?: boolean
}

/**
 * Hook for fetching simulation summary
 */
export function useSimSummary({
  filters,
  enabled = true,
}: UseSimSummaryParams = {}) {
  return useQuery<SimSummary[]>({
    queryKey: simulationKeys.summary(filters),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters?.assistant_ids?.length) {
        params.assistant_ids = filters.assistant_ids
      }
      if (filters?.tag_ids?.length) {
        params.tag_ids = filters.tag_ids
      }

      const response = await api.get<SimSummary[]>('/simulations/summary', {
        params,
      })
      return response.data
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute
  })
}
